import { useMemo } from 'react';

import { useGetTools } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import type { ITool } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { useConfigStore } from 'state/store/config/useConfigStore';
import {
  PROMOTED_CONNECTORS_PAGE_SIZE,
  providers,
  selectPromotedConnectors,
} from './connectors-dictionary';
import { useEnabledConnectorNamesState } from './use-enabled-connectors';

type ConnectorToolTab = 'api' | 'mcp_server' | 'composio' | 'open_connector';

/**
 * Tools for a picker tab. On the Connector tab, enabled open connectors are listed first
 * and replace the Composio tool with the same provider.
 */
export function useConnectorTabTools(
  toolType: ConnectorToolTab,
  pageTools: ITool[],
  enabled = true
) {
  const projectKey = useConfigStore((state) => state.configurationSettings?.blocksKey || '');
  const { names: enabledConnectorNames, isPending: isDictionaryPending } =
    useEnabledConnectorNamesState();
  const loadPromoted = enabled && toolType === 'composio';
  const promotedQuery = {
    tool_type: 'open_connector',
    page: 1,
    page_size: PROMOTED_CONNECTORS_PAGE_SIZE,
    project_key: projectKey,
  } as const;
  const featuredConnectors = useGetTools(
    { ...promotedQuery, created_by: 'catalog' },
    { enabled: loadPromoted }
  );
  const myConnectors = useGetTools(
    { ...promotedQuery, with_system_tool: false },
    { enabled: loadPromoted }
  );

  const tools = useMemo(() => {
    if (toolType === 'open_connector') {
      return pageTools.filter((tool) => tool.type === 'open_connector');
    }
    if (toolType !== 'composio') return pageTools;

    // Featured and My Tools are chosen separately, so a catalog Outlook does not hide
    // the user's own Outlook open connector.
    const featuredTools = featuredConnectors.data?.tools ?? [];
    const featuredIds = new Set(featuredTools.map((tool) => tool.id));
    const promoted = [
      ...selectPromotedConnectors(featuredTools, enabledConnectorNames),
      ...selectPromotedConnectors(
        (myConnectors.data?.tools ?? []).filter((tool) => !featuredIds.has(tool.id)),
        enabledConnectorNames
      ),
    ];
    const covered = new Set(promoted.flatMap(providers));
    return [...promoted, ...pageTools.filter((tool) => !providers(tool).some((name) => covered.has(name)))];
  }, [pageTools, enabledConnectorNames, featuredConnectors.data?.tools, myConnectors.data?.tools, toolType]);

  const isLoading =
    loadPromoted && (isDictionaryPending || featuredConnectors.isLoading || myConnectors.isLoading);

  return { tools, isLoading };
}
